import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import api from '../api';
import { createVoucher, updateVoucher } from '../redux/slices/vouchersSlice';
import { toApiDate, toNullableNumber } from '../utils/voucherUtils';

const EMPTY_FORM = {
  name: '',
  organizerId: '',
  active: true,
  discountType: 'PERCENT',
  discountValue: '',
  validFrom: null,
  validUntil: null,
  minTicketCount: '',
  maxDiscountedTicketCount: '',
  minTicketPrice: '',
  maxTicketPrice: '',
  eventScope: 'ALL_ORGANIZER_EVENTS',
  selectedEvents: [],
};

const getInitialForm = (voucher) => {
  if (!voucher) return EMPTY_FORM;

  return {
    name: voucher.name || '',
    organizerId: voucher.organizers_id || '',
    active: Boolean(voucher.active),
    discountType: voucher.discount_type || 'PERCENT',
    discountValue: voucher.discount_value ?? '',
    validFrom: voucher.valid_from ? dayjs(voucher.valid_from) : null,
    validUntil: voucher.valid_until ? dayjs(voucher.valid_until) : null,
    minTicketCount: voucher.min_eligible_ticket_count ?? '',
    maxDiscountedTicketCount: voucher.max_discounted_ticket_count ?? '',
    minTicketPrice: voucher.min_eligible_ticket_price ?? '',
    maxTicketPrice: voucher.max_eligible_ticket_price ?? '',
    eventScope: voucher.event_scope || 'ALL_ORGANIZER_EVENTS',
    selectedEvents: (voucher.events || []).map((event) => ({
      ...event,
      title: event.name,
      label: event.date ? `${event.name} - ${dayjs(event.date).format('DD.MM.YYYY HH:mm')}` : event.name,
    })),
  };
};

const loadOrganizerEvents = async (organizerId) => {
  const events = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const { data } = await api.get('/events/selector', {
      params: {
        organizer_id: organizerId || undefined,
        status: 'SELLING,NOT_SELLING',
        page,
        limit: 100,
      },
    });
    events.push(...data.events);
    hasMore = data.hasMore;
    page += 1;
  }

  return events;
};

export default function VoucherDrawer({ open, onClose, voucher, onSaved }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const organizers = useSelector((state) => state.organizers.data);
  const isSaving = useSelector((state) => state.vouchers.isLoading);
  const [form, setForm] = useState(EMPTY_FORM);
  const [eventOptions, setEventOptions] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const isEditing = Boolean(voucher);
  const canLoadEvents = user.role === 'organizer' || Boolean(form.organizerId);

  useEffect(() => {
    if (open) setForm(getInitialForm(voucher));
  }, [open, voucher]);

  useEffect(() => {
    if (!open || !canLoadEvents) {
      setEventOptions([]);
      return;
    }

    let active = true;
    setEventsLoading(true);
    loadOrganizerEvents(user.role === 'admin' ? form.organizerId : undefined)
      .then((events) => {
        if (active) setEventOptions(events);
      })
      .catch(() => {
        if (active) {
          toast.error(t('Could not load eligible events'), { position: toast.POSITION.BOTTOM_RIGHT });
        }
      })
      .finally(() => {
        if (active) setEventsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [canLoadEvents, form.organizerId, open, t, user.role]);

  const validation = useMemo(() => {
    const discount = Number(form.discountValue);
    const minCount = toNullableNumber(form.minTicketCount);
    const maxCount = toNullableNumber(form.maxDiscountedTicketCount);
    const minPrice = toNullableNumber(form.minTicketPrice);
    const maxPrice = toNullableNumber(form.maxTicketPrice);

    return {
      organizer: user.role === 'admin' && !form.organizerId,
      name: !form.name.trim(),
      discount: !Number.isFinite(discount) || discount <= 0 || (form.discountType === 'PERCENT' && discount > 100),
      dates: Boolean(form.validFrom && form.validUntil && form.validUntil.isBefore(form.validFrom)),
      counts: Boolean(minCount !== null && maxCount !== null && maxCount < minCount),
      prices: Boolean(minPrice !== null && maxPrice !== null && maxPrice < minPrice),
      events: form.eventScope === 'SELECTED_EVENTS' && form.selectedEvents.length === 0,
    };
  }, [form, user.role]);

  const isInvalid = Object.values(validation).some(Boolean);

  const update = (field) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleOrganizerChange = (event) => {
    setForm((current) => ({
      ...current,
      organizerId: event.target.value,
      selectedEvents: [],
    }));
  };

  const handleClose = () => {
    if (isSaving) return;
    setForm(EMPTY_FORM);
    setEventOptions([]);
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isInvalid) return;

    const payload = {
      name: form.name.trim(),
      discount_type: form.discountType,
      discount_value: Number(form.discountValue),
      valid_from: toApiDate(form.validFrom),
      valid_until: toApiDate(form.validUntil),
      min_eligible_ticket_count: toNullableNumber(form.minTicketCount),
      max_discounted_ticket_count: toNullableNumber(form.maxDiscountedTicketCount),
      min_eligible_ticket_price: toNullableNumber(form.minTicketPrice),
      max_eligible_ticket_price: toNullableNumber(form.maxTicketPrice),
      event_scope: form.eventScope,
      event_ids: form.eventScope === 'SELECTED_EVENTS' ? form.selectedEvents.map(({ id }) => id) : [],
    };

    if (!isEditing) {
      payload.active = form.active;
      if (user.role === 'admin') payload.organizers_id = Number(form.organizerId);
    }

    try {
      const saved = await dispatch(
        isEditing ? updateVoucher({ voucherId: voucher.id, payload }) : createVoucher(payload)
      ).unwrap();
      toast.success(t(isEditing ? 'Voucher updated successfully' : 'Voucher created successfully'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      handleClose();
      onSaved?.(saved);
    } catch {
      toast.error(t('Voucher could not be saved'), { position: toast.POSITION.BOTTOM_RIGHT });
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Toolbar />
      <Toolbar>
        <Typography variant="button">{t(isEditing ? 'Edit voucher' : 'Create voucher')}</Typography>
      </Toolbar>
      <Divider />
      <Box component="form" onSubmit={handleSubmit} sx={{ width: 420, px: 2, pb: 2 }}>
        <Typography sx={{ mt: 2 }} variant="subtitle1">
          {t('Campaign')}
        </Typography>
        {user.role === 'admin' && (
          <FormControl
            required
            fullWidth
            margin="normal"
            size="small"
            disabled={isEditing}
            error={validation.organizer}
          >
            <InputLabel>{t('Organizer')}</InputLabel>
            <Select value={form.organizerId} label={t('Organizer')} onChange={handleOrganizerChange}>
              {organizers.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          required
          fullWidth
          margin="normal"
          size="small"
          label={t('Name')}
          value={form.name}
          onChange={update('name')}
          error={validation.name}
          inputProps={{ maxLength: 128 }}
        />
        {!isEditing && (
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))}
              />
            }
            label={t('Enabled')}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>{t('Discount type')}</InputLabel>
            <Select value={form.discountType} label={t('Discount type')} onChange={update('discountType')}>
              <MenuItem value="PERCENT">{t('Percentage')}</MenuItem>
              <MenuItem value="FIXED">{t('Fixed amount')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            required
            fullWidth
            margin="normal"
            size="small"
            type="number"
            label={t('Discount value')}
            value={form.discountValue}
            onChange={update('discountValue')}
            error={validation.discount}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: 0, max: form.discountType === 'PERCENT' ? 100 : undefined, step: 0.01 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{form.discountType === 'PERCENT' ? '%' : t('Lei')}</InputAdornment>
              ),
            }}
          />
        </Box>
        <Typography sx={{ mt: 3, mb: 1 }} variant="subtitle1">
          {t('Validity')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
            <DateTimePicker
              label={t('Valid from')}
              value={form.validFrom}
              onChange={update('validFrom')}
              slotProps={{ textField: { fullWidth: true, size: 'small', error: validation.dates } }}
            />
            <IconButton
              size="small"
              disabled={!form.validFrom}
              aria-label={`${t('Clear')} ${t('Valid from')}`}
              onClick={() => setForm((current) => ({ ...current, validFrom: null }))}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
            <DateTimePicker
              label={t('Valid until')}
              value={form.validUntil}
              onChange={update('validUntil')}
              minDateTime={form.validFrom || undefined}
              slotProps={{ textField: { fullWidth: true, size: 'small', error: validation.dates } }}
            />
            <IconButton
              size="small"
              disabled={!form.validUntil}
              aria-label={`${t('Clear')} ${t('Valid until')}`}
              onClick={() => setForm((current) => ({ ...current, validUntil: null }))}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {validation.dates && <FormHelperText error>{t('End date must be after start date')}</FormHelperText>}
        <Typography sx={{ mt: 3 }} variant="subtitle1">
          {t('Eligibility rules')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            margin="normal"
            size="small"
            type="number"
            label={t('Minimum eligible tickets')}
            value={form.minTicketCount}
            onChange={update('minTicketCount')}
            error={validation.counts}
            inputProps={{ min: 1, step: 1 }}
          />
          <TextField
            fullWidth
            margin="normal"
            size="small"
            type="number"
            label={t('Maximum discounted tickets')}
            value={form.maxDiscountedTicketCount}
            onChange={update('maxDiscountedTicketCount')}
            error={validation.counts}
            inputProps={{ min: 1, step: 1 }}
          />
        </Box>
        <FormHelperText>
          {t('The minimum activates the voucher; the maximum limits how many tickets receive the discount.')}
        </FormHelperText>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            margin="normal"
            size="small"
            type="number"
            label={t('Minimum ticket price')}
            value={form.minTicketPrice}
            onChange={update('minTicketPrice')}
            error={validation.prices}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ endAdornment: <InputAdornment position="end">Lei</InputAdornment> }}
          />
          <TextField
            fullWidth
            margin="normal"
            size="small"
            type="number"
            label={t('Maximum ticket price')}
            value={form.maxTicketPrice}
            onChange={update('maxTicketPrice')}
            error={validation.prices}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ endAdornment: <InputAdornment position="end">Lei</InputAdornment> }}
          />
        </Box>
        <Typography sx={{ mt: 3 }} variant="subtitle1">
          {t('Eligible events')}
        </Typography>
        <RadioGroup
          value={form.eventScope}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              eventScope: event.target.value,
              selectedEvents: event.target.value === 'ALL_ORGANIZER_EVENTS' ? [] : current.selectedEvents,
            }))
          }
        >
          <FormControlLabel
            value="ALL_ORGANIZER_EVENTS"
            control={<Radio />}
            label={t('All organizer events, including future events')}
          />
          <FormControlLabel value="SELECTED_EVENTS" control={<Radio />} label={t('Selected events')} />
        </RadioGroup>
        {form.eventScope === 'SELECTED_EVENTS' && (
          <Autocomplete
            multiple
            loading={eventsLoading}
            options={eventOptions}
            value={form.selectedEvents}
            onChange={(_, value) => setForm((current) => ({ ...current, selectedEvents: value }))}
            isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
            getOptionLabel={(option) => option.label || option.title || option.name || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Events')}
                error={validation.events}
                helperText={validation.events ? t('Select at least one event') : t('Archived events are excluded')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {eventsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button fullWidth size="small" variant="outlined" onClick={handleClose} disabled={isSaving}>
            {t('Cancel')}
          </Button>
          <Button fullWidth size="small" type="submit" variant="contained" disabled={isInvalid || isSaving}>
            {t(isEditing ? 'Save' : 'Create voucher')}
            {isSaving && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
